<template>
    <form method="POST" :action="action" @submit="handleSubmit" class="my-8">
        <input type="hidden" name="_token" :value="csrfToken" />
        <div class="mb-4">
            <otp-input
                v-model="otpCode"
                :has-error="hasError"
                :error-message="errorMessage"
            ></otp-input>
            <input
                id="code"
                name="code"
                type="hidden"
                :value="otpCode"
                required
            />
        </div>
        <div class="flex justify-end mb-4">
            <button type="submit" class="button main rounded-md">
                {{ submitText }}
            </button>
        </div>
    </form>
</template>

<script lang="ts" setup>
import { ref, defineProps, watch } from "vue";
import OtpInput from "../ui/forms/OtpInput.vue";
import { analytics } from "@/services/segment.ts";

const props = defineProps<{
    action: string;
    submitText: string;
    redirectUrl?: string;
}>();

const otpCode = ref("");
const hasError = ref(false);
const errorMessage = ref("");
const csrfToken =
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
        ?.content || "";

// Clear error when user starts typing
watch(otpCode, () => {
    if (hasError.value) {
        hasError.value = false;
        errorMessage.value = "";
    }
});

const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    // Always set the code value from otpCode
    formData.set("code", otpCode.value);

    // Clear any previous errors
    hasError.value = false;
    errorMessage.value = "";

    try {
        const response = await fetch(props.action, {
            method: "POST",
            headers: {
                "X-CSRF-TOKEN": csrfToken,
                Accept: "application/json",
            },
            body: formData,
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok) {
            analytics.track("2FA Successful Authentication");
            if (props.redirectUrl) {
                window.location.href = props.redirectUrl;
                return;
            }
            form.submit();
        } else {
            analytics.track("2FA Unsuccessful Authentication");
            console.error('Authentication failed:', response.status, data);

            // Set error state
            hasError.value = true;

            // Extract error message from response
            if (data.message) {
                errorMessage.value = data.message;
            } else if (data.errors) {
                // Handle validation errors
                const firstError = Object.values(data.errors)[0];
                errorMessage.value = Array.isArray(firstError) ? firstError[0] : "Invalid code. Please try again.";
            } else {
                errorMessage.value = "Invalid code. Please try again.";
            }
        }
    } catch (error) {
        console.error('Request error:', error);
        analytics.track("2FA Unsuccessful Authentication");

        // Set error state for network errors
        hasError.value = true;
        errorMessage.value = "An error occurred. Please try again.";
    }
};
</script>
